/**
 * 与属性面板 `<input accept>` 对齐的屏幕贴图文件校验；拖放与面板共用 {@link applyScreenMaterialFileToActiveLayer} 写入 store。
 */

const HDR_NAME = /\.hdr$/i
const MP4_NAME = /\.mp4$/i
/** MIME 为空时的常见栅格后缀（与面板 `image/*` 意图一致） */
const RASTER_NAME = /\.(png|jpe?g|gif|webp|bmp|avif|svg|tiff?|ico)$/i

/** 供面板多文件逻辑与 `accept` 对齐：是否为 MP4 视频屏幕素材 */
export function isVideoMp4ScreenMaterialFile(file: File): boolean {
  const t = (file.type || '').toLowerCase()
  return t === 'video/mp4' || MP4_NAME.test(file.name)
}

export function isHdrScreenMaterialFile(file: File): boolean {
  if (HDR_NAME.test(file.name)) return true
  return (file.type || '').toLowerCase() === 'image/vnd.radiance'
}

export function isRasterImageScreenMaterialFile(file: File): boolean {
  const t = (file.type || '').toLowerCase()
  if (t.startsWith('image/')) return true
  return RASTER_NAME.test(file.name)
}

/**
 * 是否允许作为当前图层的屏幕贴图文件（与 `MockupControlsSection` 的 `accept` 一致）。
 *
 * @param deviceTemplateId - 图层 `deviceTemplateId`
 * @param file - 本地文件
 */
export function isScreenMaterialFileAcceptedForLayer(deviceTemplateId: string, file: File): boolean {
  if (deviceTemplateId === 'text-plane') return false
  if (deviceTemplateId === 'image-plane') {
    if (isHdrScreenMaterialFile(file)) return false
    return isRasterImageScreenMaterialFile(file) || isVideoMp4ScreenMaterialFile(file)
  }
  return isRasterImageScreenMaterialFile(file) || isHdrScreenMaterialFile(file) || isVideoMp4ScreenMaterialFile(file)
}

/**
 * 在拖入多文件时取第一个通过校验的文件。
 *
 * @param files - `dataTransfer.files` 或数组
 * @param deviceTemplateId - 目标图层模板 id
 */
export function pickFirstAcceptedScreenMaterialFile(
  files: FileList | readonly File[] | null | undefined,
  deviceTemplateId: string,
): File | null {
  if (!files) return null
  const n = 'length' in files ? files.length : 0
  for (let i = 0; i < n; i++) {
    const f = files[i]
    if (f && isScreenMaterialFileAcceptedForLayer(deviceTemplateId, f)) return f
  }
  return null
}

/** 供工具模块引用，避免依赖 Pinia 实例类型 */
export type MockupScreenMaterialSetter = {
  setActiveLayerScreenMaterial(
    fileName: string | null,
    objectUrl: string | null,
    file?: File | null,
  ): void
}

/**
 * 与面板 `onMaterialFile` 一致：写入当前 `activeLayer` 的屏幕材质（含 `URL.createObjectURL`）。
 *
 * @param store - mockup 编辑器 store
 * @param file - 选中文件；`null` 表示清除
 */
export function applyScreenMaterialFileToActiveLayer(store: MockupScreenMaterialSetter, file: File | null): void {
  store.setActiveLayerScreenMaterial(
    file ? file.name : null,
    file ? URL.createObjectURL(file) : null,
    file ?? undefined,
  )
}
